package dev.lingvo.shared.screenshots.render

import dev.lingvo.shared.TextDirection
import dev.lingvo.shared.screenshots.doc.BlendMode
import dev.lingvo.shared.screenshots.geom.Rect

/** Same meaning as `CanvasRenderingContext2D.transform(a, b, c, d, e, f)`: x' = a x + c y + e, y' = b x + d y + f. */
data class Affine(val a: Double, val b: Double, val c: Double, val d: Double, val e: Double, val f: Double) {
    companion object {
        val IDENTITY: Affine = Affine(1.0, 0.0, 0.0, 1.0, 0.0, 0.0)
    }
}

/** Path segments. There are no arcs: a circle is four cubics with k = [Paths.KAPPA]. */
sealed interface PathOp {
    data class Move(val x: Double, val y: Double) : PathOp
    data class Line(val x: Double, val y: Double) : PathOp
    data class Cubic(val c1x: Double, val c1y: Double, val c2x: Double, val c2y: Double, val x: Double, val y: Double) : PathOp
    data object Close : PathOp
}

class Path(val ops: List<PathOp>) {
    override fun equals(other: Any?): Boolean = other is Path && other.ops == ops
    override fun hashCode(): Int = ops.hashCode()
    override fun toString(): String = "Path($ops)"
}

enum class FillRule { NONZERO, EVENODD }

enum class Join { MITER, ROUND, BEVEL }

enum class Cap { BUTT, ROUND, SQUARE }

/** A paint with palette tokens already resolved to ARGB (`0xAARRGGBB`). Gradient geometry is in current user space. */
sealed interface ResolvedPaint {
    data class Solid(val argb: Long) : ResolvedPaint
    data class Linear(val x0: Double, val y0: Double, val x1: Double, val y1: Double, val stops: List<Pair<Double, Long>>) : ResolvedPaint
    data class Radial(val cx: Double, val cy: Double, val r: Double, val stops: List<Pair<Double, Long>>) : ResolvedPaint
    data class Noise(val seed: Int, val amount: Double) : ResolvedPaint
}

data class StrokeStyle(
    val width: Double,
    val join: Join = Join.ROUND,
    val cap: Cap = Cap.ROUND,
    val dash: List<Double> = emptyList(),
)

/** A shadow in surface (device) pixels: canvas ignores the current transform for shadow offset and blur. */
data class DeviceShadow(val dx: Double, val dy: Double, val blur: Double, val argb: Long)

/** [css] is the complete canvas `font` value, built only from catalog family names so a document cannot inject CSS. */
data class ResolvedFont(val css: String, val sizePx: Double, val stack: List<String>, val weight: Int, val italic: Boolean)

enum class CanvasAlign { LEFT, CENTER, RIGHT }

interface ImageHandle {
    val key: String
    val width: Int
    val height: Int
}

/**
 * The drawing surface the renderer targets, shaped like `CanvasRenderingContext2D` so the browser adapter maps it
 * one to one and a recorder can stand in for it in tests (SPEC-B 2.1).
 *
 * State (transform, alpha, blend, shadow, clip) is saved and restored as a stack, as on canvas. A `restore()`
 * without a matching `save()` is ignored, also as on canvas.
 */
interface Surface {
    val width: Int
    val height: Int

    fun save()
    fun restore()

    /** Post-multiplies the current transform: [m] applies to coordinates first. */
    fun concat(m: Affine)
    fun setBlend(mode: BlendMode)

    /** Absolute `globalAlpha`; the renderer multiplies nested opacities itself. */
    fun setAlpha(alpha: Double)

    /** Already in device pixels; null clears. */
    fun setShadow(shadow: DeviceShadow?)
    fun clip(path: Path, rule: FillRule = FillRule.NONZERO)
    fun fill(path: Path, paint: ResolvedPaint, rule: FillRule = FillRule.NONZERO)
    fun stroke(path: Path, paint: ResolvedPaint, style: StrokeStyle)
    fun drawImage(image: ImageHandle, src: Rect, dst: Rect)

    /** [y] is the alphabetic baseline. The surface runs bidi with [direction] on the logical string. */
    fun fillText(
        text: String,
        x: Double,
        y: Double,
        font: ResolvedFont,
        paint: ResolvedPaint,
        align: CanvasAlign,
        direction: TextDirection,
        letterSpacingPx: Double,
    )
}

/** Ascent and descent in pixels at the font's size. */
data class VerticalMetrics(val ascent: Double, val descent: Double)

/** Measures text exactly as the paired [Surface] draws it. */
interface TextMeasurer {
    fun width(text: String, font: ResolvedFont, letterSpacingPx: Double): Double
    fun verticalMetrics(font: ResolvedFont): VerticalMetrics
}
